import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import { RECORD_DOMAINS, type RecordDomain } from "permissions/_views/records/record-permissions-path";
import type { RecordGrants } from "permissions/_views/records/use-record-grants";
import { type NameState, targetStateText } from "permissions/_views/targets/permission-target-state";
import { recordPage } from "permissions/_views/wording/permission-wording";

/**
 * What names a record and says what its rows are, shared by the record page, the Who has access
 * drawer and the groups under a proposal or a work agreement.
 */

/** The kind a domain's records are granted by, from the kinds endpoint. */
export function recordKindOf(kinds: readonly PermissionKind[], domain: RecordDomain): PermissionKind | undefined {
  return kinds.find((kind) => kind.resourceType === RECORD_DOMAINS[domain]);
}

/** The record's name for the title, or why it has none. */
export function recordTitle(target: NameState, noun: string): string {
  if (target.state === "found") return target.name;
  if (target.state === "loading") return "…";
  return targetStateText(target, noun) ?? noun;
}

/**
 * The caption's second line: whether the rows are every way in. A conversation opens only through a
 * grant on it. A document opens through access to every document with no grant on it. A proposal or a
 * work agreement has a grant written for each way in but one: the view kept after leaving the work.
 * A calendar event, an organization agreement and either template open only through a grant on them
 * too (#6174).
 */
export function standingAccessLine(domain: RecordDomain, noun: string): string {
  switch (domain) {
    case "forums":
      return recordPage.everyWayIn(noun);
    case "calendar-events":
    case "organization-agreements":
    case "fee-schedule-templates":
    case "team-templates":
      return recordPage.onlyThroughGrants(noun);
    case "documents":
      return recordPage.documentStandingAccess;
    case "proposals":
    case "work-agreements":
      return recordPage.workStandingAccess(noun);
  }
}

/**
 * The caption: how many people hold a live grant on the record, never "can open it", and what else
 * opens it.
 */
export function RecordCaption(props: Readonly<{ domain: RecordDomain; grants: RecordGrants; noun: string }>) {
  return (
    <Box aria-live="polite">
      <Typography variant="body2" color="text.secondary">
        {props.grants.holderCount === undefined ? <Skeleton width="30%" /> : recordPage.holdersCaption(props.noun, props.grants.holderCount)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {standingAccessLine(props.domain, props.noun)}
      </Typography>
    </Box>
  );
}
